import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@monksflow/monks-ui';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import {
  projectCampaignsQueryOptions,
  projectPerformanceQueryOptions,
} from '@/entities/media-campaign';
import {
  PerformanceToolbar,
  usePerformanceFilters,
  availableFilterOptions,
} from '@/features/performance-filters';
import { ReportMenu } from '@/features/performance-reports';
import { useScope } from '@/features/performance-scope';
import { track } from '@/shared/analytics';
import { ROUTE_PATTERNS, ROUTES } from '@/shared/config/routes';
import { LeavesTogether, SETTLED_EXIT_SECONDS } from '@/shared/ui/animations';
import { usePerformanceOpened } from './usePerformanceOpened';

/**
 * The four analysis views, in tab order. A single array so the strip and the route
 * mapping cannot drift — add a view here and both update together. The label is held as a
 * key rather than a resolved string, since a module-scope array cannot resolve one without
 * freezing it at import.
 */
const VIEWS = [
  /* eslint-disable i18next/no-literal-string -- `value` is the view's URL path segment and analytics token, not copy */
  {
    value: 'overview',
    labelKey: 'projectDetail.performance.overview',
    path: ROUTES.PROJECT_PERFORMANCE,
  },
  {
    value: 'pacing',
    labelKey: 'projectDetail.performance.pacing',
    path: ROUTES.PROJECT_PERFORMANCE_PACING,
  },
  {
    value: 'creative',
    labelKey: 'projectDetail.performance.creative',
    path: ROUTES.PROJECT_PERFORMANCE_CREATIVE,
  },
  {
    value: 'audience',
    labelKey: 'projectDetail.performance.audienceAndPlacement',
    path: ROUTES.PROJECT_PERFORMANCE_AUDIENCE,
  },
  /* eslint-enable i18next/no-literal-string */
] as const;

type PerformanceView = (typeof VIEWS)[number]['value'];

/**
 * Project Detail → Performance.
 *
 * The tabs are routes rather than local state, so a tab is linkable and survives a
 * reload — the same reason Delivery nests its job screen. Campaign setup is reached
 * from the header action instead, since it configures what the views report rather
 * than being one of them, and report downloads sit in the overflow menu beside the
 * filters for the same reason: they act on the current window, they are not a place.
 */
export function Performance() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const projectId = project.platformProjectId;

  // Every view but Overview owns a distinct trailing path segment, so a suffix match
  // identifies it. Manage campaigns is a sixth, unrelated route under this same section —
  // null there so the strip shows no active tab, rather than misreporting Overview.
  const isManageCampaignsRoute = pathname.endsWith(
    `/${ROUTE_PATTERNS.PROJECT_PERFORMANCE_CAMPAIGNS}`,
  );
  const activeView: PerformanceView | null = isManageCampaignsRoute
    ? null
    : (VIEWS.find((view) => view.value !== 'overview' && pathname.endsWith(`/${view.value}`))
        ?.value ?? 'overview');

  usePerformanceOpened(projectId, isManageCampaignsRoute ? 'campaigns' : 'dashboard');

  // The filters live in the URL, so the views read the same values through their own
  // `usePerformanceFilters` call rather than having them passed down. Only the writing
  // happens here, which is why the row can sit above the routed content without the
  // views knowing it exists.
  const { filters, setFilters } = usePerformanceFilters(projectId);
  const { scope } = useScope(projectId);
  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));
  const hasCampaigns = (campaigns.data?.length ?? 0) > 0;
  // This row is the section's toolbar: four tabs that would all report the same
  // nothing, a date range over no data, and filters with nothing to narrow. It
  // stays on the campaigns route, where it is the only way back to the views.
  //
  // Held through the wait rather than gated on a settled answer: leaving on the
  // rare empty project costs one fade, while arriving on every other one would
  // push the content down the moment the campaigns landed.
  const showTabsFrame = hasCampaigns || campaigns.isLoading || activeView === null;

  const { platforms: availablePlatforms, channels: availableChannels } = useMemo(
    () => availableFilterOptions(campaigns.data),
    [campaigns.data],
  );

  // The same key Overview already asks for, so on that view this is a cache read rather
  // than a second request, and the report is built from the figures on screen. On the
  // other views it does fetch: a report is of the window and scope, not of the tab, so
  // it must not change depending on which tab happened to be open.
  const performance = useQuery(
    projectPerformanceQueryOptions(projectId, {
      startDate: filters.range.startDate,
      endDate: filters.range.endDate,
      platforms: filters.platforms,
      channels: filters.channels,
      campaignId: scope.campaignId ?? undefined,
    }),
  );

  // Falls back to a generic marker rather than dropping the campaign from the label when
  // the name has not resolved: an unnamed narrowing is still a narrowing, and a deck that
  // omits it reads as the whole project's numbers.
  const campaignScope = scope.campaignId
    ? (campaigns.data?.find((campaign) => campaign.id === scope.campaignId)?.name ??
      t('projectDetail.performance.singleCampaign'))
    : undefined;

  // `activeView` only catches a repeat once the route has caught up, and it does not:
  // navigation is async, so a second click landing before the re-render still sees the old
  // value and would fire a second track()/navigate(). This ref records the view already
  // acted on; the effect resyncs it whenever the route genuinely changes, including to a
  // view reached by other means such as browser back or forward.
  const requestedView = useRef<PerformanceView | null>(activeView);
  useEffect(() => {
    requestedView.current = activeView;
  }, [activeView]);

  const handleViewChange = (nextView: PerformanceView) => {
    if (nextView === activeView || nextView === requestedView.current) return;
    requestedView.current = nextView;
    track('performance_view_switched', { project_id: projectId, view: nextView });
    const view = VIEWS.find((candidate) => candidate.value === nextView);
    // Carrying `search` across the strip, not just the pathname: the campaign scope, the
    // date range and the platform/channel filters all live in the query string, and every
    // view reads the same params. Dropping them here would silently reset the analysis a
    // marketer had set up the moment they moved between two views of it.
    if (view) void navigate({ pathname: view.path(projectId), search });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="project-performance-section">
      {/* Plain buttons rather than monks-ui Tabs, matching JobPlatformTabs in Delivery's
          job review. Tabs defaults to a filled pill container with a sliding indicator,
          and overriding it back to an underline left the trigger's own active background
          showing through. Reproducing the markup is less code than fighting the component,
          and it is the same markup the reference already uses. */}
      <LeavesTogether present={showTabsFrame} exitSeconds={SETTLED_EXIT_SECONDS} collapse>
        <div
          className="mb-app-inset shrink-0 rounded-lg bg-card px-section text-foreground"
          data-testid="performance-tabs-frame"
        >
          {/* Tabs and filters share one row, and one bottom rule, because the filters govern
            every view rather than belonging to the one on screen. Rendered here rather than
            inside each view: three copies drifted, and two views had none at all, which read
            as though the date range did not apply to them. */}
          <div
            className="flex min-h-14 flex-wrap items-stretch justify-between gap-x-6 gap-y-2 border-b border-border-light"
            data-testid="performance-tabs-row"
          >
            <nav aria-label={t('projectDetail.performance.performanceViews')} className="flex">
              <div className="flex items-stretch gap-1" role="tablist">
                {VIEWS.map((view) => {
                  const isActive = view.value === activeView;
                  return (
                    <button
                      key={view.value}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleViewChange(view.value)}
                      data-testid={`performance-view-tab-${view.value}`}
                      // The active underline sits on the row's own rule, not below it, so the
                      // negative margin is what makes the two read as one line.
                      className={`-mb-px flex items-center border-b-2 px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        isActive
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t(view.labelKey)}
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Hidden on the manage-campaigns route: it configures which campaigns report,
              and none of these filters narrow that list. */}
            {activeView !== null && (
              <div className="flex items-center gap-1">
                {/* Sits with the filters rather than in a header of its own: it
                  configures which campaigns report, which is what the filters
                  then narrow. */}
                <Button variant="outline" size="sm" asChild>
                  <Link to={ROUTES.PROJECT_PERFORMANCE_CAMPAIGNS(projectId)}>
                    {t('projectDetail.performance.manageCampaigns')}
                  </Link>
                </Button>
                <PerformanceToolbar
                  filters={filters}
                  availablePlatforms={availablePlatforms}
                  availableChannels={availableChannels}
                  onChange={setFilters}
                />
                <ReportMenu
                  projectId={projectId}
                  projectName={project.title}
                  performance={performance.data}
                  window={filters.range}
                  platforms={filters.platforms}
                  campaignScope={campaignScope}
                />
              </div>
            )}
          </div>
        </div>
      </LeavesTogether>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card"
        data-testid="performance-content-frame"
      >
        {/* The active tab's body is the nested route, so the URL is the single source of
            truth for which tab is showing. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
          <Outlet context={{ project }} />
        </div>
      </div>
    </div>
  );
}
